import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Download, FileText, Upload, X } from "lucide-react";
import moment from "moment";
import {
  downloadAllTemplates,
  downloadAllTemplatesJson,
} from "../../helper-components/helper-function";
import { useFormikContext } from "formik";
import errorToast from "../../helper-components/error-toast";

// The project's Tailwind theme replaces the default palette, so colours come
// from the app's global CSS variables (arbitrary values) - not bg-blue-50 etc.
const C = {
  heading: "text-[var(--heading-color)]",
  muted: "text-[var(--description-color)]",
  primaryText: "text-[var(--primary)]",
  border: "border-[var(--light-border-color)]",
};

const GrnCsvImport = ({
  grnCsvImportOpen,
  setGrnCsvImportOpen,
  setIsModalOpen,
  handleFileUpload = () => {},
  tempalteArray = [],
  prefixName = "gort",
  setCommonImportFileType,
}) => {
  const { values } = useFormikContext();
  // The navbar always stores a one-entry vendorTemplate, even for a vendor
  // with no template (expectedHeaders undefined) - check for real columns.
  const hasVendorTemplate = (values?.vendorTemplate || []).some(
    (t) => Array.isArray(t?.expectedHeaders) && t.expectedHeaders.length > 0
  );
  const vendorLabel = values?.cmr_name?.label || "";
  const docLabel = prefixName === "gort" ? "GRN" : "invoice";

  const [format, setFormat] = useState("vendor");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const fileInputRef = useRef(null);

  // Fresh state every time the dialog opens.
  useEffect(() => {
    if (grnCsvImportOpen) {
      setFormat(hasVendorTemplate ? "vendor" : "common-csv");
      setFile(null);
      setDragOver(false);
      setShowHelp(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnCsvImportOpen]);

  const close = () => setGrnCsvImportOpen(false);

  const formats = [
    {
      key: "vendor",
      title: "Vendor template",
      badge: "CSV",
      hint: !vendorLabel
        ? "Select a vendor to use their import template."
        : hasVendorTemplate
        ? `Upload the file exactly as ${vendorLabel} sends it — using the column mapping set up in Settings › Import Templates.`
        : `No template mapped for ${vendorLabel} yet — set one up in Settings › Import Templates.`,
      onDownload: () => {
        if (hasVendorTemplate) {
          downloadAllTemplates(values?.vendorTemplate);
        } else {
          errorToast("Template is not Available to Download");
        }
      },
    },
    {
      key: "common-csv",
      title: "Standard format",
      badge: "CSV",
      hint: "The common layout — works for any vendor.",
      onDownload: () => downloadAllTemplates(tempalteArray),
    },
    {
      key: "common-json",
      title: "Standard format",
      badge: "JSON",
      hint: "For system-to-system exports and integrations.",
      onDownload: () => downloadAllTemplatesJson(tempalteArray),
    },
  ];

  const pickFile = (picked) => {
    if (!picked) return;
    if (!picked.name?.toLowerCase().endsWith(".csv")) {
      errorToast("Please choose a .csv file");
      return;
    }
    setFile(picked);
  };

  const isVendor = format === "vendor";
  const receivedDate = values?.[`${prefixName}_delivery_date`];

  const onPrimary = () => {
    if (isVendor) {
      if (!file) return;
      if (!values?.cmr_id) {
        errorToast("Please select Vendor");
        return;
      }
      // The page's upload handler reads a file-input change event.
      handleFileUpload({ target: { files: [file], value: "" } }, "vendor");
      close();
      return;
    }
    setCommonImportFileType?.(format === "common-json" ? "json" : "csv");
    setIsModalOpen(true);
    close();
  };

  const sectionTitle = `text-[11px] font-semibold tracking-wider uppercase mb-2.5 ${C.muted}`;

  return (
    <Transition show={grnCsvImportOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={close}
      >
        <div className="flex min-h-screen px-4 text-center min-w-full">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="mx-auto my-auto w-full max-w-[600px] text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
                <div className="flex items-start gap-3">
                  <span className="flex items-center justify-center shrink-0 w-10 h-10 rounded-xl bg-[var(--row-selection-color)] text-[var(--primary)]">
                    <Upload size={20} />
                  </span>
                  <div>
                    <h2 className={`text-base font-semibold ${C.heading}`}>
                      {prefixName === "gort"
                        ? "Import GRN items"
                        : "Import purchase items"}
                    </h2>
                    <p className={`text-xs mt-0.5 ${C.muted}`}>
                      Add line items to this {docLabel} from a file. You'll
                      review every row before anything is saved.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={close}
                  className={`p-1.5 rounded-lg ${C.muted} hover:bg-[var(--row-hover-color)] hover:text-[var(--primary)]`}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Context strip */}
              <div
                className={`flex flex-wrap items-center gap-x-6 gap-y-1 px-6 py-2.5 bg-[var(--row-hover-color)] border-y ${C.border} text-xs ${C.muted}`}
              >
                <span>
                  Vendor{" "}
                  <span className={`font-semibold ${C.heading}`}>
                    {vendorLabel || "Not selected"}
                  </span>
                </span>
                {receivedDate && (
                  <span>
                    Received{" "}
                    <span className={`font-semibold ${C.heading}`}>
                      {moment(receivedDate).format("DD-MM-YYYY")}
                    </span>
                  </span>
                )}
              </div>

              <div className="px-6 py-5 flex flex-col gap-5">
                {/* 1 · File format */}
                <section>
                  <h3 className={sectionTitle}>1 · File format</h3>
                  <div className="flex flex-col gap-2.5" role="radiogroup">
                    {formats.map((opt) => {
                      const selected = format === opt.key;
                      return (
                        <div
                          key={opt.key}
                          role="radio"
                          aria-checked={selected}
                          tabIndex={0}
                          onClick={() => setFormat(opt.key)}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              setFormat(opt.key);
                            }
                          }}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors focus:outline-none ${
                            selected
                              ? "border-[var(--import-border)] bg-[var(--row-hover-color)] shadow-[0_0_0_1px_var(--import-border)]"
                              : `${C.border} bg-white hover:border-[var(--import-border)] hover:bg-[var(--import-bg)]`
                          }`}
                        >
                          <span
                            className={`flex items-center justify-center shrink-0 w-[18px] h-[18px] rounded-full border-2 ${
                              selected
                                ? "border-[var(--primary)]"
                                : "border-[var(--button-border-color)]"
                            }`}
                          >
                            {selected && (
                              <span className="w-2 h-2 rounded-full bg-[var(--primary)]" />
                            )}
                          </span>

                          <div className="flex flex-col flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={`text-sm font-semibold ${
                                  selected ? C.primaryText : C.heading
                                }`}
                              >
                                {opt.title}
                              </span>
                              <span
                                className={`px-1.5 py-px rounded text-[10px] font-semibold tracking-wide ${
                                  opt.badge === "JSON"
                                    ? "text-[var(--download-fg)] bg-[var(--download-bg)] border border-[var(--download-border)]"
                                    : "text-[var(--import-button-fg)] bg-[var(--import-button-bg)] border border-[var(--import-button-border)]"
                                }`}
                              >
                                {opt.badge}
                              </span>
                            </div>
                            <span className={`text-xs mt-0.5 ${C.muted}`}>
                              {opt.hint}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              opt.onDownload();
                            }}
                            title="Download Template"
                            className={`flex items-center gap-1 shrink-0 h-8 px-2.5 rounded-md text-xs font-semibold ${C.primaryText} hover:bg-[var(--row-selection-color)]`}
                          >
                            <Download size={14} />
                            Template
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>

                {/* 2 · Upload file */}
                <section>
                  <h3 className={sectionTitle}>2 · Upload file</h3>

                  {isVendor ? (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => {
                          pickFile(e.target.files?.[0]);
                          e.target.value = null;
                        }}
                      />
                      {file ? (
                        <div
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-[var(--import-button-border)] bg-[var(--import-button-bg)]`}
                        >
                          <span className="flex items-center justify-center shrink-0 w-9 h-9 rounded-lg bg-white text-[var(--import-button-fg)]">
                            <FileText size={18} />
                          </span>
                          <div className="flex flex-col flex-1 min-w-0">
                            <span
                              className={`text-sm font-medium truncate ${C.heading}`}
                            >
                              {file.name}
                            </span>
                            <span className={`text-xs ${C.muted}`}>
                              {(file.size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFile(null)}
                            className="text-xs font-semibold text-[var(--remove-color)] px-2.5 h-8 rounded-md hover:bg-white"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => fileInputRef.current?.click()}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              fileInputRef.current?.click();
                            }
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOver(true);
                          }}
                          onDragLeave={() => setDragOver(false)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setDragOver(false);
                            pickFile(e.dataTransfer.files?.[0]);
                          }}
                          className={`flex flex-col items-center justify-center gap-2 px-4 py-7 rounded-xl border-2 border-dashed cursor-pointer transition-colors border-[var(--import-border)] ${
                            dragOver
                              ? "bg-[var(--row-selection-color)]"
                              : "bg-[var(--row-hover-color)] hover:bg-[var(--row-selection-color)]"
                          }`}
                        >
                          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-[var(--primary)] shadow-sm">
                            <Upload size={18} />
                          </span>
                          <span className={`text-sm ${C.heading}`}>
                            Drag &amp; drop your file here, or{" "}
                            <span
                              className={`font-semibold underline ${C.primaryText}`}
                            >
                              browse
                            </span>
                          </span>
                          <span className={`text-xs ${C.muted}`}>
                            Accepts .csv in the vendor's own format
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border border-[var(--import-border)] bg-[var(--row-hover-color)] text-xs ${C.muted}`}
                    >
                      <FileText
                        size={18}
                        className={`shrink-0 mt-0.5 ${C.primaryText}`}
                      />
                      <div>
                        The standard format uses three{" "}
                        {format === "common-json" ? ".json" : ".csv"} files —{" "}
                        <span className={`font-semibold ${C.heading}`}>
                          Order Data, Item Data and Batch Data
                        </span>
                        . You'll upload all three on the next step.
                      </div>
                    </div>
                  )}
                </section>

                {showHelp && (
                  <div
                    className={`px-4 py-3 rounded-xl bg-[var(--row-hover-color)] border ${C.border} text-xs leading-5 ${C.muted}`}
                  >
                    <span className={`font-semibold ${C.heading}`}>
                      How importing works:
                    </span>{" "}
                    pick the format your file is in, download its template if
                    you need one, then upload. Every row is checked against your
                    product master and shown for review — nothing is saved to
                    the {docLabel} until you confirm.
                  </div>
                )}
              </div>

              {/* Footer */}
              <div
                className={`flex items-center justify-between gap-3 px-6 py-4 border-t ${C.border}`}
              >
                <button
                  type="button"
                  onClick={() => setShowHelp((v) => !v)}
                  className={`text-xs font-semibold underline ${C.primaryText}`}
                >
                  How importing works
                </button>
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={close}
                    className={`h-10 px-5 rounded-lg border border-[var(--button-border-color)] bg-white text-sm font-medium ${C.heading} hover:bg-[var(--row-hover-color)]`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onPrimary}
                    disabled={isVendor && !file}
                    className="h-10 px-5 rounded-lg text-sm font-semibold text-[var(--primary-button-fg-color)] bg-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isVendor ? "Upload & review" : "Continue"}
                  </button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GrnCsvImport;
